import { MERCHANT_TERMS } from '@field-support/shared';
import type { formsAr } from './forms.ar';

/** Form builder copy (English). Must cover exactly the same keys as `formsAr`. */
export const formsEn: typeof formsAr = {
  forms: {
    title: 'Form builder',
    description: 'The visit form agents fill in, and how each field maps to a Zoho Desk ticket.',
    statusUnsaved: 'Draft has unsaved changes',
    statusSaved: 'Draft saved – not published in this session',
    statusPublished: 'Draft published',
    statusBlocked: 'Publishing blocked – fix the listed problems',
    draft: 'Draft',
    fields: 'Fields',
    formName: 'Form name',
    subjectTemplate: 'Ticket subject template',
    subjectTemplateHint: 'Available placeholders (field keys also work):',
    addField: 'Add field',
    saveDraft: 'Save draft',
    publish: 'Publish new version',
    publishConfirm: 'The saved draft becomes a new version. Agents receive it on their next sync; visits on older versions keep working.',
    published: 'Version {{version}} published',
    publishTitle: 'Publish a new version?',
    copyOverUnsavedTitle: 'Discard unsaved changes?',
    copyOverUnsavedConsequence: 'Loading this version replaces the draft, so your unsaved changes are lost.',
    copyOverUnsavedConfirm: 'Discard and load',
    basedOn: 'Based on version {{version}}',
    issues: 'Problems to fix before publishing',
    warnings: 'Warnings',
    versions: 'Published versions',
    activeVersion: 'Active',
    loadIntoDraft: 'Copy to draft',
    fieldKey: 'Key',
    label: 'Label',
    labelAr: 'Label (Arabic)',
    labelEn: 'Label (English)',
    labelEnHint: 'Optional. Shown when the app is in English; otherwise the Arabic label is used.',
    newFieldLabel: 'New field {{n}}',
    newOptionLabel: 'Option {{n}}',
    problems: {
      cannotSave: 'The draft was not saved. Fix these first:',
      cannotPublish: 'The draft cannot be published yet. See the issues below.',
      needsFix: 'Needs fixing',
      label: 'the Arabic label is required',
      labelEn: 'the English label is too long',
      fieldKey: 'the key must be lowercase letters, digits and underscores, starting with a letter',
      option: 'option {{n}} needs both a label and a value',
      captureHint: 'the capture hint is too long (200 characters at most)',
      formName: 'The form name is required',
      fieldNumber: 'Field {{n}}',
      beforeSave: 'The draft cannot be saved until these are fixed:',
      patternInvalid: 'the regex pattern is not a valid regular expression',
      patternNested: 'the regex pattern repeats a repeated group (like (a+)*), which can hang validation; simplify it',
      rules: 'a validation rule is out of range',
    },
    definition: {
      duplicateKey: 'Duplicate field key "{{key}}"',
      noOptions: 'A choice field needs at least one option',
      duplicateOptions: 'Option values must be unique',
      minMaxLength: 'Min length must not exceed max length',
      minMax: 'Minimum must not exceed maximum',
      minMaxItems: 'Min items must not exceed max items',
      photoNoZoho: 'Photo fields go to the ticket as attachments and cannot map to a ticket field',
      zohoNameWithoutTarget: 'Remove the Zoho field name or choose where it is sent',
      zohoNameRequired: 'Choose the Zoho field this is sent to',
      zohoCustomPrefix: 'Custom field API names start with "cf_"',
      zohoSystemAttribute: 'Choose one of the listed ticket attributes',
      zohoDuplicate: 'Zoho field "{{name}}" is already mapped by "{{other}}"',
      selfDependency: 'A field cannot depend on itself',
      conditionEarlier: 'The condition refers to "{{key}}", which must be an earlier field',
      conditionNoValue: `Conditions can only refer to fields that hold a value (not photos, ${MERCHANT_TERMS.en.oneLower} or branch)`,
      conditionValue: 'Choose a value for this condition',
      conditionList: 'IN needs a list of values',
      branchOnProject: 'A once-per-trip field cannot follow a per-project field',
      merchantScope: `${MERCHANT_TERMS.en.one} and Branch are asked once per trip; they cannot be per-project`,
      roleScope: 'A reporting role is only for per-project fields',
      roleType: 'The “{{role}}” role needs a field of another type',
      roleUsed: 'The “{{role}}” role is already used by "{{other}}"',
      toggleType: 'Only yes/no fields can be shown as a toggle',
      dependsType: 'Only choice fields can depend on another field',
      dependsEarlier: 'Its options depend on "{{key}}", which must be an earlier field',
      dependsParentType: 'The parent field must be a single choice',
      dependsOption: '"{{value}}" is not an option any more',
      dependsEmpty: 'Required, but nothing is offered for: {{options}}',
      prefillType: 'Only single-choice fields can be auto-filled',
      prefillSelf: 'A field cannot be auto-filled from itself',
      prefillEarlier: 'Auto-filled from "{{key}}", which must be an earlier field',
      prefillSourceType: 'Auto-fill needs a single-choice source field',
      prefillNoMatch: 'No option of "{{key}}" has the same key as an option of this field, so nothing would be filled',
      projectRoleMissing: 'Per-project fields need one Project field (single choice, role Project) to name each project block',
      oneMerchant: `Only one ${MERCHANT_TERMS.en.one} field is allowed`,
      oneBranch: 'Only one Branch field is allowed',
      branchNeedsMerchant: `A Branch field requires a ${MERCHANT_TERMS.en.one} field`,
      merchantConditional: `${MERCHANT_TERMS.en.one} and Branch fields cannot be conditional`,
      noFields: 'The form has no fields',
      zohoNotLoaded: 'Zoho ticket fields were not loaded, so mapped field names were not checked against Zoho',
      zohoValueType: 'This field type cannot be sent to Zoho as {{valueType}}',
      branchMapping: 'The Branch field must be mapped to a Zoho field (needed to correct branches on tickets)',
      notMapped: 'Not sent to Zoho; its value is kept in the visit log only',
      zohoMissing: 'Zoho has no ticket field named "{{name}}"',
      zohoIncompatible: 'Zoho field type "{{zohoType}}" does not accept {{valueType}} values',
      zohoPicklist: 'A Zoho picklist only accepts its allowed values; use a single-choice field with matching options',
      zohoMultiselect: 'A Zoho multi-select expects values from its allowed list',
      zohoText: 'The value will be sent to a text field',
      zohoSingleValue:
        'Zoho field "{{name}}" holds one value; on a trip with several projects it gets the main visit’s value and the others go in the ticket description',
      zohoMandatory: 'Zoho marks "{{label}}" ({{name}}) as mandatory but no form field maps to it',
      unknown: 'Check this field’s settings',
      unknownForm: 'Check the form settings',
    },
    editField: 'Edit field',
    applyField: 'Apply to draft',
    viewVersion: 'View',
    versionTitle: 'Version {{version}}',
    type: 'Type',
    required: 'Required',
    placeholder: 'Placeholder',
    helpText: 'Help text',
    options: 'Options',
    optionLabel: 'Option label',
    optionValue: 'Value',
    addOption: 'Add option',
    removeOption: 'Remove option',
    delimiter: 'Delimiter',
    characters: 'Allowed characters',
    rules: 'Validation',
    minLength: 'Min length',
    maxLength: 'Max length',
    min: 'Minimum',
    max: 'Maximum',
    maxItems: 'Max items',
    pattern: 'Regex pattern',
    errorMessage: 'Error message',
    photoQuality: 'Photo quality',
    photoQualityLevel: 'Quality level',
    photoQualityLevels: { STANDARD: 'Standard', HIGH: 'High' },
    photoExpectedSize: 'Expected size',
    photoQualityAdvice:
      'Use High only where small text has to be readable — serial numbers, invoices, data plates. Every High photo is about twice the size, which costs the server bandwidth and the agent their data allowance.',
    captureHint: 'Capture hint',
    captureHintHelp: 'Shown to the agent above the camera button. Framing matters more than resolution for readability.',
    visibleWhen: 'Show only when',
    addCondition: 'Add condition',
    removeCondition: 'Remove condition',
    zohoTarget: 'Send to Zoho as',
    zohoField: 'Zoho field',
    zohoValueType: 'Value type',
    zohoFieldsUnavailable: 'Zoho ticket fields could not be loaded; type the API name.',
    loadZohoFields: 'Load Zoho fields',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeField: 'Remove field',
    preview: 'Mobile preview',
    previewNote: 'Preview only – nothing is submitted.',
    systemMappings: 'Background fields (GPS, time, agent)',
    saveMappings: 'Save mappings',
    version: 'Version',
    publishedAt: 'Published',
    publishedBy: 'By',
    fieldsVisits: 'Fields / visits',
    fieldTypes: {
      TEXT: 'Text',
      TEXTAREA: 'Long text',
      NUMBER: 'Number',
      CURRENCY: 'Amount',
      PHONE: 'Mobile number',
      SELECT: 'Single choice',
      MULTI_SELECT: 'Multiple choice',
      BOOLEAN: 'Yes / No',
      ATTACHMENT_IMAGE: 'Photos',
      BRAND_SELECT: `${MERCHANT_TERMS.en.one}`,
      BRANCH_SELECT: 'Branch',
    },
    targets: { NONE: 'Not sent', CUSTOM_FIELD: 'Custom field', SYSTEM_FIELD: 'Ticket attribute' },
    scope: 'Asked',
    scopeHint: 'Per project: asked again for every additional project visit of a trip. Once per trip: merchant, branch, manager.',
    scopes: { VISIT: 'Once per trip (branch)', PROJECT: 'Per project' },
    role: 'Reporting role',
    roleHint: 'Tells the visits table, the filters and the Zoho aggregates what this field means. Each role once.',
    roles: {
      none: 'None',
      PROJECT: 'Project (names the block)',
      VISIT_TYPE: 'Visit type',
      SERVICE: 'Service',
      SERVICE_DETAIL: 'Service detail',
      ISSUE_FLAG: 'Issue found',
      ISSUE_TYPE: 'Issue type',
      ISSUES: 'Issues',
      COMMENT: 'Comment',
    },
    optionLabelEn: 'English label',
    optionKeysHint: 'The value is the stable key that is stored, sent to Zoho and reported on. Labels can be renamed later; keep the key.',
    multiChoice: 'Several answers allowed',
    dependsOn: 'Options depend on',
    dependsOnParent: 'Parent field',
    dependsOnNone: 'Independent (all options)',
    dependsOnHint: 'For each option of the parent, tick the options offered. Changing the parent on the phone clears this answer.',
    dependentFor: 'When “{{option}}” is chosen',
    prefillFrom: 'Auto-fill from',
    prefillNone: 'No auto-fill',
    prefillHint: 'Filled with the option that has the same key. The agent can change it; a changed value is kept and marked.',
    booleanStyle: 'Shown as',
    booleanStyles: { CHOICE: 'Yes / No choice', TOGGLE: 'Toggle, off by default' },
    reveals: 'Fields shown while the toggle is on',
    revealsHint: 'Each ticked field gets the condition “this toggle is on”.',
  },
  preview: {
    selectBrand: `Choose ${MERCHANT_TERMS.en.oneLower}`,
    selectBranch: 'Choose branch',
    photosPlaceholder: 'Photos are taken with the camera in the app',
    addPhoto: 'Add photo',
    valid: 'All required fields are valid',
    chooseFirst: 'Choose {{parent}} first',
    overridden: 'Differs from its auto-fill source ({{value}}); kept as chosen.',
    projectBlock: 'Project block (repeated for every additional visit)',
  },
  formIssues: {
    REQUIRED: 'Required',
    INVALID_OPTION: 'Choose one of the options',
    INVALID_PHONE: 'Enter an 11-digit mobile number starting with 01',
    INVALID_CHARACTERS: 'Contains characters that are not allowed',
    PATTERN_MISMATCH: 'Does not match the expected format',
    INVALID_TYPE: 'Invalid value',
    OPTION_NOT_FOR_PARENT: 'Not an option for the choice above',
    TOO_SHORT: 'Too short',
    TOO_LONG: 'Too long',
    TOO_SMALL: 'Below the minimum',
    TOO_LARGE: 'Above the maximum',
    TOO_FEW_ITEMS: 'Too few',
    TOO_MANY_ITEMS: 'Too many',
    WRONG_LENGTH: 'Wrong length',
    UNKNOWN_FIELD: 'Unknown field',
    default: 'Not valid',
  },
};
